import { useEscape } from "@/hooks/useKeyboardShortcut";
import { useScrollLock } from "@/hooks/useScrollLock";
import { cn } from "@/lib/utils";
import React from "react";
import { createPortal } from "react-dom";

import { SheetHeader } from "./SheetHeader";

export interface BottomSheetViewProps {
  children: React.ReactNode;
  isOpen: boolean;
  onClose: () => void;
  className?: string;
  /**
   * Tailwind padding-bottom class for the content area. In the app the
   * BottomSheet wrapper supplies "ad-gutter-pad", which reserves the measured
   * height of the anchored ad; standalone it can be omitted.
   */
  paddingBottomClassName?: string;
  /**
   * Given, the sheet draws its header row — the minimize button, the title
   * and the sheet's one action — above the content, and lays the content out
   * as the column below it.
   */
  header?: SheetHeaderConfig;
}

export type SheetHeaderConfig = {
  title: string;
  /** Accessible name for the minimize button. */
  closeLabel: string;
  action?: React.ReactNode;
  /** Defaults to the sheet's onClose; set it when minimizing differs from
      dismissing. */
  onClose?: () => void;
};

interface ActionItemProps {
  icon: React.ReactNode;
  label: string;
  /** Optional second line under the label — say when to reach for the action. */
  description?: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
}

export const ActionItem: React.FC<ActionItemProps> = ({
  icon,
  label,
  description,
  onClick,
  disabled,
}) => {
  return (
    <button
      className={cn(
        "flex flex-row gap-x-4 text-dark-secondary text-left",
        description ? "items-start py-3" : "items-center h-12",
        disabled && "text-dark-disabled",
      )}
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      disabled={disabled}
    >
      <span className={cn("size-6 mx-2 shrink-0", description && "mt-0.5")}>
        {icon}
      </span>
      <span className="flex flex-col gap-y-0.5">
        <p>{label}</p>
        {description && (
          <span className="text-sm text-dark-tertiary">{description}</span>
        )}
      </span>
    </button>
  );
};

export const BottomSheetView: React.FC<BottomSheetViewProps> = ({
  children,
  isOpen,
  onClose,
  className,
  paddingBottomClassName,
  header,
}) => {
  useEscape(onClose, isOpen, true);
  // The page under the sheet stays put: a swipe that starts on the backdrop,
  // or runs past the end of the sheet's own content, must not scroll it.
  useScrollLock(isOpen);
  if (!isOpen) return null;

  /* Portaled to the body: a sheet opened from inside a positioned, z-indexed
     band would otherwise be trapped in that band's stacking context and sit
     under whatever the page draws above it. */
  return createPortal(
    // Portaled in the DOM, but React still bubbles events up the component
    // tree: without this, a click anywhere in the sheet (backdrop included)
    // reaches whatever row or card opened it and triggers that too.
    <div
      className="fixed inset-0 z-50 flex items-end"
      onClick={(e) => e.stopPropagation()}
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 touch-none bg-black/50"
        onClick={onClose}
      />

      {/* Bottom Sheet Content */}
      <div
        className={cn(
          "relative mx-auto w-full bg-neutral-800 rounded-t-xl shadow-soft-dark-bottom max-w-150 touch-device:max-w-full flex flex-col items-center py-2 transition-all duration-200 translate-y-0 starting:translate-y-full",
          className,
        )}
      >
        {/* Content — also the scroll container when the sheet is given a fixed
            height via className (grow/min-h-0 make it fill, overflow scrolls).
            Scrolling here keeps the ad padding-bottom at the end of the
            scrolled flow rather than cutting the viewport short. */}
        <div
          className={cn(
            "w-full grow min-h-0 overflow-y-auto overscroll-y-contain",
            paddingBottomClassName,
          )}
        >
          {header ? (
            <div className="flex min-h-full w-full flex-col">
              <SheetHeader
                title={header.title}
                onClose={header.onClose ?? onClose}
                closeLabel={header.closeLabel}
                action={header.action}
              />
              {children}
            </div>
          ) : (
            children
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default BottomSheetView;
